import {
  StyleFlexStoryblok,
  StyleGridStoryblok,
  StyleMarginsStoryblok,
  StylePaddingsStoryblok,
  StyleSizesStoryblok,
  StyleStoryblok,
  StylesTextStoryblok,
  StyleGeneralStoryblok,
  StyleLayoutStoryblok,
} from '@components/storyblok/types';
import sitePath from './sitePath';

export function extractDimensionsFromURL(url: string): { width: number; height: number } | null {
  const regex = /(\d+)x(\d+)/;
  const match = url?.match(regex);

  if (match) {
    return { width: parseInt(match[1]), height: parseInt(match[2]) };
  } else {
    return null;
  }
}

export function forceExtractDimensionsFormURL(
  url: string,
  fallback: { width: number; height: number } = { width: 1200, height: 800 },
): { width: number; height: number } {
  let width = fallback?.width;
  let height = fallback?.height;

  if (!url) return fallback;

  try {
    const dimensions = extractDimensionsFromURL(url);
    width = dimensions?.width;
    height = dimensions?.height;
  } catch (e) {
    console.error('^^^ RichText NODE_IMAGE', e);
  }

  return { width, height };
}

export function returnPathWithStartingSlash(path: string): string {
  return path.startsWith('/') ? path : `/${path}`;
}

/**
 * URL configuration depends on the server setup:
 *  - `https://example.com/page/`
 *  - `https://example.com/page`
 *
 * These are often treated as different pages because one represents a **directory** (with a trailing slash)
 * and the other an **actual file** (without a trailing slash).
 *
 * In **Storyblok**, when creating a folder, it does not have direct navigability unless files are added inside.
 * If an index file is set inside the folder to simulate directory behavior, the `full_slug` will end with a slash (`/`).
 * However, this is **not correct** for the canonical URL, as it should **not** have a trailing slash.
 * This is because the navigation should be `terminal.io/folder` and **not** `terminal.io/folder/`.
 *
 * This function ensures the canonical URL format by always removing the trailing slash if present.
 *
 * @param path - The URL path to be formatted.
 * @returns The correctly formatted canonical URL.
 */
export function createCanonicalPath(path: string): string {
  const url = sitePath(returnPathWithStartingSlash(path));
  return url.endsWith('/') ? url.slice(0, -1) : url;
}

/**
 * If a component can have the style blok, it fetches it and returns the style props
 * @deprecated use selectBlokStyles instead
 */
export function selectStoryblokStyleBlok<T extends { style?: StyleStoryblok[] }>(blok: T) {
  const blokStyle: StyleStoryblok = blok?.style?.find(
    (eachStyle) => eachStyle?.component?.toLowerCase() === 'style',
  );

  return {
    marginTop: blokStyle?.marginTop,
    marginBottom: blokStyle?.marginBottom,
    marginRight: blokStyle?.marginRight,
    marginLeft: blokStyle?.marginLeft,
    textAlign: blokStyle?.textAlign,
    alignItems: blokStyle?.alignItems,
    justifyContent: blokStyle?.justifyContent,
  };
}

export function selectBlokStyles<
  T extends {
    style?: (
      | StyleMarginsStoryblok
      | StyleFlexStoryblok
      | StyleSizesStoryblok
      | StylesTextStoryblok
      | StyleGridStoryblok
      | StylePaddingsStoryblok
      | StyleGeneralStoryblok
      | StyleLayoutStoryblok
    )[];
  },
>(blok: T) {
  const marginComponent = blok.style?.find(
    (eachStyle) => eachStyle.component === 'styleMargins',
  ) as StyleMarginsStoryblok;

  const marginStyles = marginComponent
    ? {
        marginTop: selectResponsiveStyle(marginComponent.marginTop),
        marginBottom: selectResponsiveStyle(marginComponent.marginBottom),
        marginLeft: selectResponsiveStyle(marginComponent.marginLeft),
        marginRight: selectResponsiveStyle(marginComponent.marginRight),
      }
    : {};

  const paddingComponent = blok.style?.find(
    (eachStyle) => eachStyle.component === 'stylePaddings',
  ) as StylePaddingsStoryblok;

  const paddingStyles = paddingComponent
    ? {
        paddingTop: selectResponsiveStyle(paddingComponent.paddingTop),
        paddingBottom: selectResponsiveStyle(paddingComponent.paddingBottom),
        paddingLeft: selectResponsiveStyle(paddingComponent.paddingLeft),
        paddingRight: selectResponsiveStyle(paddingComponent.paddingRight),
      }
    : {};

  const sizesStyleComponent = blok.style?.find(
    (eachStyle) => eachStyle.component === 'styleSizes',
  ) as StyleSizesStoryblok;

  const sizesStyle = sizesStyleComponent
    ? {
        height: selectResponsiveStyle(sizesStyleComponent.height),
        maxHeight: selectResponsiveStyle(sizesStyleComponent.maxHeight),
        minHeight: selectResponsiveStyle(sizesStyleComponent.minHeight),
        width: selectResponsiveStyle(sizesStyleComponent.width),
        maxWidth: selectResponsiveStyle(sizesStyleComponent.maxWidth),
        minWidth: selectResponsiveStyle(sizesStyleComponent.minWidth),
      }
    : {};

  const textStyleComponent = blok.style?.find(
    (eachStyle) => eachStyle.component === 'stylesText',
  ) as StylesTextStoryblok;

  const textStyles = textStyleComponent
    ? {
        textAlign: selectResponsiveStyle(textStyleComponent.textAlign),
        fontWeight: selectResponsiveStyle(textStyleComponent.fontWeight),
        fontStyle: selectResponsiveStyle(textStyleComponent.fontStyle),
        textDecoration: selectResponsiveStyle(textStyleComponent.textDecoration),
      }
    : {};

  const flexComponent = blok.style?.find(
    (eachStyle) => eachStyle.component === 'styleFlex',
  ) as StyleFlexStoryblok;

  const flexStyles = flexComponent
    ? {
        gap: selectResponsiveStyle(flexComponent.gap),
        alignItems: selectResponsiveStyle(flexComponent.alignItems),
        alignContent: selectResponsiveStyle(flexComponent.alignContent),
        justifyContent: selectResponsiveStyle(flexComponent.justifyContent),
        justifyItems: selectResponsiveStyle(flexComponent.justifyItems),
        direction: selectResponsiveStyle(flexComponent.direction),
        flexWrap: selectResponsiveStyle(flexComponent.flexWrap),
        flex: selectResponsiveStyle(flexComponent.flex),
      }
    : {};

  const gridComponent = blok.style?.find(
    (eachStyle) => eachStyle.component === 'styleGrid',
  ) as StyleGridStoryblok;

  const gridStyles = gridComponent
    ? {
        spacing: selectResponsiveStyle(gridComponent.gap),
        spacingX: selectResponsiveStyle(gridComponent.horizontalGap),
        spacingY: selectResponsiveStyle(gridComponent.verticalGap),
        alignItems: selectResponsiveStyle(gridComponent.alignItems),
        columns: selectResponsiveStyle(gridComponent.columns),
      }
    : {};

  const generalComponent = blok.style?.find(
    (eachStyle) => eachStyle.component === 'styleGeneral',
  ) as StyleGeneralStoryblok;

  const generalStyles = generalComponent
    ? {
        backgroundColor: selectResponsiveStyle(generalComponent.backgroundColor),
      }
    : {};

  const layoutComponent = blok.style?.find(
    (eachStyle) => eachStyle.component === 'styleLayout',
  ) as StyleLayoutStoryblok;

  const layoutStyles = layoutComponent
    ? {
        backgroundColor: layoutComponent.backgroundColor.value,
        border: layoutComponent.border,
        borderColor: layoutComponent.borderColor.valye,
        borderRadius: layoutComponent.borderRadius,
        borderTop: layoutComponent.borderTop,
        borderBottom: layoutComponent.borderBottom,
        borderLeft: layoutComponent.borderLeft,
        borderRight: layoutComponent.borderRight,
        boxShadow: layoutComponent.boxShadow,
      }
    : {};
  return {
    marginStyles,
    sizesStyle,
    flexStyles,
    textStyles,
    /**
     * SimpleGrid Styles
     */
    gridStyles,
    paddingStyles,
    generalStyles,
    layoutStyles,
  };
}

export function selectResponsiveStyle(value?: string): string | string[] {
  if (!value) {
    return undefined;
  }
  return value?.includes(',') ? value.split(',') : value;
}
